import { useState, useEffect, useRef } from 'react'
import { FloppyDisk, Car, CheckCircle, XCircle, Info, Warning, ArrowClockwise, LinkSimple } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Transporte',
      criteria_code: 'TRA4-DATA',
      criteria_name: 'TRA4 Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveData error:', error.message)
  } catch (e) { console.error('saveData exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'TRA4-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

// ── Fórmula oficial BREEAM ES NA3 (idéntica a TRA1Calculator.js)
// Verificada contra Calculadora BREEAM TRA-1.xls oficial 2026-06-04.
// Por servicio j: fu = 60 / (2 × (d/80 + 30/F + factor_fiab))
// Por modo (rank logic): Total = max(fu) + 0.5 × sum(resto)
// IA = sum(Total_EDF) sobre todos los modos
const TRA1_TRANSPORT = [
  { label: 'Autobús urbano',       factor: 2,    maxDist: 650  },
  { label: 'Autobús interurbano',  factor: 2,    maxDist: 650  },
  { label: 'Tranvía',              factor: 1,    maxDist: 1000 },
  { label: 'Metro',                factor: 0.75, maxDist: 1000 },
  { label: 'Cercanías / Tren',     factor: 0.75, maxDist: 1000 },
  { label: 'Ferry',                factor: 2,    maxDist: 1000 },
]
const WALKING_SPEED = 80  // m/min

function computeIAFromTra1(projectId) {
  if (!projectId) return null
  try {
    const saved = JSON.parse(localStorage.getItem(`tra1_${projectId}`) || 'null')
    if (!saved?.nodos) return null
    // 1) Agrupar frec_umbral por modo (todos los servicios de todos los nodos
    //    del mismo tipo en la misma "bolsa" para la rank logic).
    const byMode = new Map()
    let hasAnyDistance = false
    for (const nodo of saved.nodos) {
      const d = parseFloat(nodo.distancia)
      if (!d || d <= 0) continue
      hasAnyDistance = true
      const t = TRA1_TRANSPORT.find(x => x.label === nodo.tipo) || TRA1_TRANSPORT[0]
      if (d > t.maxDist) continue
      const tCam = d / WALKING_SPEED
      for (const fStr of (nodo.servicios || [])) {
        const f = parseFloat(fStr)
        if (!f || f <= 0) continue
        const tProg = 30 / f
        const tMedio = tProg + t.factor
        const tAcceso = tCam + tMedio
        const fu = 60 / (2 * tAcceso)
        if (!byMode.has(nodo.tipo)) byMode.set(nodo.tipo, [])
        byMode.get(nodo.tipo).push(fu)
      }
    }
    if (!hasAnyDistance) return null
    // 2) Rank logic por modo: max(fu) + 0.5 × sum(resto)
    let ia = 0
    for (const frecs of byMode.values()) {
      if (!frecs.length) continue
      const sorted = [...frecs].sort((a, b) => b - a)
      ia += sorted[0] + sorted.slice(1).reduce((s, v) => s + v * 0.5, 0)
    }
    return parseFloat(ia.toFixed(2))
  } catch { return null }
}

// ── Tabla 28 BREEAM ES NC 2015 TRA 4
// Ratios "1 plaza por cada X usuarios" según IA y tipo edificio
// { ia_col: [x_1pt, x_2pts] }
const RATIOS = {
  // Tipos 1-5 excluidos comerciales
  'tipo1_5': {
    '<4':  [3, 4],
    '4-8': [4, 5],
    '>=8': [5, 6],
  },
  // Edificios docentes
  'docente': {
    '<4':  [15, 20],
    '4-8': [20, 25],
    '>=8': [25, 30],
  },
}

const BUILDING_TYPES = [
  { key: 'tipo1_5',   label: 'Tipos 1–5 (excluidos comerciales)' },
  { key: 'docente',   label: 'Edificio docente' },
  { key: 'sanitario', label: 'Edificio sanitario' },
  { key: 'noaplica',  label: 'Tipo 3 solo comercio / Tipo 7 (no aplica)' },
]

const INP_STYLE = {
  padding: '0.3rem 0.45rem', fontSize: '0.72rem',
  border: '1px solid #C8D8C0', borderRadius: '4px',
  background: 'white', fontFamily: "'IBM Plex Mono', monospace",
  boxSizing: 'border-box', color: '#2C2017',
}

function iaCol(ia) {
  const v = parseFloat(ia)
  if (isNaN(v)) return null
  if (v < 4)  return '<4'
  if (v < 8)  return '4-8'
  return '>=8'
}

function calcMaxStandard(tipoKey, ia, nUsuarios) {
  const col = iaCol(ia)
  if (!col) return null
  const ratios = RATIOS[tipoKey]
  if (!ratios) return null
  const [x1, x2] = ratios[col]
  const n = parseFloat(nUsuarios)
  if (!n || n <= 0) return null
  return {
    max1pt: Math.floor(n / x1),
    max2pts: Math.floor(n / x2),
    x1, x2,
  }
}

function calcMaxSanitario({ trabajadores, camas, salas, camasGuardia }) {
  const t = parseFloat(trabajadores) || 0
  const c = parseFloat(camas) || 0
  const s = parseFloat(salas) || 0
  const cg = parseFloat(camasGuardia) || 0
  return Math.floor(t / 4) + Math.floor(c / 4) + 2 * s + Math.floor(cg / 2)
}

function calcPts({ tipoKey, ia, nUsuarios, plazasProyecto, trabajadores, camas, salas, camasGuardia }) {
  if (tipoKey === 'noaplica') return { pts: null, noAplica: true }

  const p = parseFloat(plazasProyecto)
  if (isNaN(p) || p < 0) return { pts: null }

  if (tipoKey === 'sanitario') {
    const maxTotal = calcMaxSanitario({ trabajadores, camas, salas, camasGuardia })
    const cumple = p <= maxTotal
    return { pts: cumple ? 2 : 0, maxTotal, isSanitario: true }
  }

  const res = calcMaxStandard(tipoKey, ia, nUsuarios)
  if (!res) return { pts: null }

  const { max1pt, max2pts, x1, x2 } = res
  let pts = 0
  if (p <= max2pts) pts = 2
  else if (p <= max1pt) pts = 1

  return { pts, max1pt, max2pts, x1, x2 }
}

function initState() {
  return {
    tipoKey: 'tipo1_5',
    ia: '',
    nUsuarios: '',
    plazasProyecto: '',
    // sanitario
    trabajadores: '',
    camas: '',
    salas: '',
    camasGuardia: '',
  }
}

// ── Fila de campo etiquetado — definida FUERA del componente
function FieldRow({ label, hint, value, onChange, placeholder, width = '100px', min = 0 }) {
  return (
    <div>
      <label style={{ fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.2rem' }}>
        {label}
      </label>
      {hint && <div style={{ fontSize: '0.56rem', color: '#9A8A7A', marginBottom: '0.2rem' }}>{hint}</div>}
      <input
        type="number"
        min={min}
        step="any"
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ ...INP_STYLE, width }}
      />
    </div>
  )
}

export default function TRA4Calculator({ projectId, onSave, saving }) {
  const storageKey = `tra4_${projectId}`
  const [savedOk, setSavedOk] = useState(false)
  const [state, setState] = useState(() => {
    if (!projectId) return initState()
    try {
      const saved = JSON.parse(localStorage.getItem(`tra4_${projectId}`) || 'null')
      return saved || initState()
    } catch { return initState() }
  })
  // IA sincronizado desde TRA1 (recalculado en cada render desde localStorage TRA1)
  const [tra1IA, setTra1IA] = useState(() => computeIAFromTra1(projectId))

  // Ref-guard: bloquea el persist hasta que el load de Supabase termine (BUG-race).
  const hasLoaded = useRef(false)

  // Cargar desde Supabase al montar
  useEffect(() => {
    if (!projectId) return
    loadDataFromSupabase(projectId).then(remote => {
      if (remote) setState(remote)
      hasLoaded.current = true
    })
  }, [projectId]) // eslint-disable-line

  // Persistir: localStorage inmediato + Supabase debounced 1.5s (BUG-009)
  const supabaseSaveTimer = useRef(null)
  useEffect(() => {
    if (!projectId) return
    if (!hasLoaded.current) return
    localStorage.setItem(storageKey, JSON.stringify(state))
    if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current)
    supabaseSaveTimer.current = setTimeout(() => saveDataToSupabase(projectId, state), 1500)
    return () => { if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current) }
  }, [state, projectId]) // eslint-disable-line

  // Flush al desmontar
  useEffect(() => {
    return () => {
      if (!projectId) return
      if (!hasLoaded.current) return
      localStorage.setItem(storageKey, JSON.stringify(state))
      if (supabaseSaveTimer.current) {
        clearTimeout(supabaseSaveTimer.current)
        saveDataToSupabase(projectId, state)
      }
    }
  }, [projectId, state]) // eslint-disable-line

  function saveNow() {
    localStorage.setItem(storageKey, JSON.stringify(state))
    saveDataToSupabase(projectId, state)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  const refreshTra1IA = () => setTra1IA(computeIAFromTra1(projectId))

  const set = (field, val) => setState(prev => ({ ...prev, [field]: val }))

  // El IA efectivo es el de TRA1 (si disponible), sino el manual de state.ia
  const iaEfectivo = tra1IA !== null ? String(tra1IA) : state.ia

  const result = calcPts({ ...state, ia: iaEfectivo })
  const { pts, max1pt, max2pts, maxTotal, isSanitario, noAplica, x1, x2 } = result
  const col = iaCol(iaEfectivo)
  const isTipo1_5orDocente = state.tipoKey === 'tipo1_5' || state.tipoKey === 'docente'
  const pActual = parseFloat(state.plazasProyecto)
  const ptsValid = pts !== null && pts !== undefined

  const colLabel = col === '<4' ? 'IA < 4' : col === '4-8' ? 'IA 4–8' : col === '>=8' ? 'IA ≥ 8' : '—'

  return (
    <div style={{ marginBottom: '1.25rem', border: '2px solid #6A9A7A', borderRadius: '6px', overflow: 'hidden' }}>

      {/* Cabecera */}
      <div style={{ background: '#2E4A38', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Car size={15} weight="fill" style={{ color: '#A8D0B8', flexShrink: 0 }} />
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: '#A8D0B8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Calculadora BREEAM ES TRA 4 — Capacidad Máxima de Aparcamiento
        </span>
      </div>

      <div style={{ padding: '0.875rem', background: '#F5FAF6' }}>

        {/* Nota */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: '#EEF4EA', border: '1px solid #B0D0B8', borderRadius: '5px', padding: '0.5rem 0.7rem', marginBottom: '1rem' }}>
          <Info size={13} style={{ color: '#3B7A50', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '0.62rem', color: '#2E4A38', lineHeight: '1.45' }}>
            Los valores de referencia dependen del IA calculado en <strong>TRA 1</strong>. Introduce el IA obtenido en TRA 1 para que la calculadora determine la capacidad máxima permitida.
          </div>
        </div>

        {/* ─── Tipo de edificio ─── */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.25rem' }}>
            Tipo de edificio
          </label>
          <select
            value={state.tipoKey}
            onChange={e => set('tipoKey', e.target.value)}
            style={{ ...INP_STYLE, width: 'auto', border: '1px solid #6A9A7A' }}
          >
            {BUILDING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>

        {/* No aplica */}
        {noAplica && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#F5F0EA', border: '1px solid #D8CCC0', borderRadius: '5px', padding: '0.75rem 1rem' }}>
            <Warning size={16} style={{ color: '#C98A2E', flexShrink: 0 }} />
            <span style={{ fontSize: '0.7rem', color: '#7A5A1A' }}>
              Este requisito <strong>no se evalúa</strong> para Tipo 3 (solo comercio) ni para Tipo 7.
            </span>
          </div>
        )}

        {/* ─── Edificios estándar (Tipos 1-5 / docentes) ─── */}
        {isTipo1_5orDocente && (
          <div style={{ border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.75rem', background: 'white', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
              {/* Campo IA — sincronizado desde TRA 1 */}
              <div>
                <label style={{ fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.2rem' }}>
                  IA del edificio
                </label>
                {tra1IA !== null ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <div style={{
                      background: tra1IA === 0 ? '#FDF5F3' : '#EAF3EC',
                      border: `1px solid ${tra1IA === 0 ? '#D4614A' : '#6A9A7A'}`,
                      borderRadius: '4px', padding: '0.3rem 0.6rem',
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.82rem', fontWeight: '700',
                      color: tra1IA === 0 ? '#D4614A' : '#1A3020', minWidth: '70px', textAlign: 'center',
                    }}>
                      {tra1IA.toFixed(2)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <LinkSimple size={10} style={{ color: tra1IA === 0 ? '#D4614A' : '#3B7A50' }} />
                        <span style={{ fontSize: '0.56rem', color: tra1IA === 0 ? '#D4614A' : '#3B7A50', fontWeight: '600' }}>
                          {tra1IA === 0 ? 'IA = 0 (distancia excede el límite)' : 'Sincronizado desde TRA 1'}
                        </span>
                      </div>
                      <button onClick={refreshTra1IA} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6A9A7A', fontSize: '0.55rem', padding: 0, fontFamily: "'DM Sans', sans-serif" }}>
                        <ArrowClockwise size={10} /> Actualizar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="number" min="0" step="any"
                      value={state.ia}
                      placeholder="ej. 2.5"
                      onChange={e => set('ia', e.target.value)}
                      style={{ ...INP_STYLE, width: '110px' }}
                    />
                    <span style={{ fontSize: '0.58rem', color: '#C98A2E' }}>⚠ Completa TRA 1 primero</span>
                  </div>
                )}
              </div>
              <FieldRow
                label="Nº usuarios del edificio"
                hint="Empleados habituales u ocupantes regulares"
                value={state.nUsuarios}
                onChange={v => set('nUsuarios', v)}
                placeholder="ej. 88"
                width="110px"
              />
              <FieldRow
                label="Plazas aparcamiento del proyecto"
                hint="Número de plazas previstas en el proyecto"
                value={state.plazasProyecto}
                onChange={v => set('plazasProyecto', v)}
                placeholder="ej. 25"
                width="110px"
              />
            </div>

            {/* Tabla de ratios con la columna activa */}
            {state.ia !== '' && col && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                  Tabla 28 — Ratios aplicables ({colLabel})
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {[
                    { label: '1 pt — 1 plaza/cada', x: x1, max: max1pt, pts: 1 },
                    { label: '2 pts — 1 plaza/cada', x: x2, max: max2pts, pts: 2 },
                  ].map(row => {
                    const active = ptsValid && (
                      (row.pts === 1 && pts === 1 && pActual <= row.max) ||
                      (row.pts === 2 && pts === 2 && pActual <= row.max)
                    )
                    return (
                      <div key={row.pts} style={{
                        flex: 1, minWidth: '140px',
                        background: active ? '#EAF3EC' : '#F8F5F0',
                        border: `1px solid ${active ? '#6A9A7A' : '#DDD5C8'}`,
                        borderRadius: '5px', padding: '0.5rem 0.7rem',
                      }}>
                        <div style={{ fontSize: '0.6rem', fontWeight: '700', color: active ? '#2E4A38' : '#7A6A5A', marginBottom: '0.2rem' }}>
                          {row.pts} punto{row.pts > 1 ? 's' : ''}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#5A4A3A', fontFamily: "'IBM Plex Mono', monospace" }}>
                          1 plaza / {row.x !== undefined ? row.x : '—'} usuarios
                        </div>
                        {state.nUsuarios && row.max !== undefined && (
                          <div style={{ fontSize: '0.62rem', color: '#3B5A47', fontWeight: '600', marginTop: '0.2rem' }}>
                            → máx. <strong>{row.max}</strong> plazas
                          </div>
                        )}
                        {active && <CheckCircle size={13} weight="fill" style={{ color: '#3B7A50', marginTop: '0.2rem' }} />}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {state.ia !== '' && !col && (
              <div style={{ fontSize: '0.62rem', color: '#9A8A7A', marginTop: '0.4rem' }}>Introduce un IA válido (número ≥ 0).</div>
            )}
          </div>
        )}

        {/* ─── Edificio sanitario ─── */}
        {state.tipoKey === 'sanitario' && (
          <div style={{ border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.75rem', background: 'white', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
              Fórmula: 1/4 trabajadores + 1/4 camas + 2× salas consulta + 1/2 camas guardia
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
              <FieldRow label="Nº trabajadores" value={state.trabajadores} onChange={v => set('trabajadores', v)} placeholder="ej. 60" width="100px" />
              <FieldRow label="Nº camas" value={state.camas} onChange={v => set('camas', v)} placeholder="ej. 40" width="100px" />
              <FieldRow label="Salas consulta/urgencias" hint="Consulta, exploración, tratamiento, terapia…" value={state.salas} onChange={v => set('salas', v)} placeholder="ej. 8" width="100px" />
              <FieldRow label="Camas personal guardia" value={state.camasGuardia} onChange={v => set('camasGuardia', v)} placeholder="ej. 10" width="100px" />
              <FieldRow label="Plazas proyecto" hint="Plazas previstas en el proyecto" value={state.plazasProyecto} onChange={v => set('plazasProyecto', v)} placeholder="ej. 35" width="100px" />
            </div>

            {maxTotal > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem', fontSize: '0.65rem', color: '#3B5A47' }}>
                <span>Capacidad máxima calculada:</span>
                <span style={{ fontWeight: '800', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.85rem', color: '#1A3020' }}>{maxTotal}</span>
                <span>plazas</span>
                <span style={{ color: '#7A6A5A' }}>
                  (⌊{state.trabajadores || 0}/4⌋ + ⌊{state.camas || 0}/4⌋ + 2×{state.salas || 0} + ⌊{state.camasGuardia || 0}/2⌋)
                </span>
              </div>
            )}
          </div>
        )}

        {/* ─── Resultados ─── */}
        {!noAplica && (
          <div style={{ background: 'white', border: '1px solid #B8D4C0', borderRadius: '6px', padding: '0.875rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.875rem' }}>

              {/* Badge puntos */}
              <div style={{ background: '#EAF3EC', border: '2px solid #2E4A38', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '100px' }}>
                <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#2E4A38', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Puntos TRA 4</div>
                <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1A3020', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                  {ptsValid ? pts : '—'}<span style={{ fontSize: '0.8rem', color: '#6A9A7A' }}>/2</span>
                </div>
              </div>

              {/* Mensaje resultado */}
              <div style={{ flex: 1 }}>
                {!ptsValid && (
                  <span style={{ fontSize: '0.7rem', color: '#9A8A7A' }}>
                    Completa los datos para calcular la puntuación.
                  </span>
                )}
                {ptsValid && pts === 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <XCircle size={15} weight="fill" style={{ color: '#D4614A' }} />
                    <span style={{ fontSize: '0.7rem', color: '#7A1A1A', fontWeight: '600' }}>
                      {isSanitario
                        ? `${pActual} plazas > máximo permitido (${maxTotal}) → 0 pts`
                        : `${pActual} plazas > máximo para 1 pt (${max1pt}) → 0 pts`}
                    </span>
                  </div>
                )}
                {ptsValid && pts === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <CheckCircle size={15} weight="fill" style={{ color: '#3B7A50' }} />
                      <span style={{ fontSize: '0.7rem', color: '#2E4A38', fontWeight: '600' }}>
                        {pActual} plazas ≤ {max1pt} (máx. 1 pt) → <strong>1 punto</strong>
                      </span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#9A8A7A', paddingLeft: '1.3rem' }}>
                      Para 2 pts: reducir a ≤ {max2pts} plazas.
                    </span>
                  </div>
                )}
                {ptsValid && pts === 2 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <CheckCircle size={15} weight="fill" style={{ color: '#3B7A50' }} />
                    <span style={{ fontSize: '0.7rem', color: '#2E4A38', fontWeight: '600' }}>
                      {isSanitario
                        ? `${pActual} plazas ≤ máximo permitido (${maxTotal}) → 2 puntos`
                        : `${pActual} plazas ≤ ${max2pts} (máx. 2 pts) → 2 puntos`}
                    </span>
                  </div>
                )}
              </div>

              {/* Resumen comparativo */}
              {ptsValid && !isSanitario && max1pt !== undefined && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.6rem', fontFamily: "'IBM Plex Mono', monospace", color: '#5A4A3A' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: '#7A6A5A' }}>Proyecto:</span>
                    <span style={{ fontWeight: '700' }}>{pActual} plazas</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: '#7A6A5A' }}>Máx. 1 pt:</span>
                    <span style={{ fontWeight: '700', color: pActual <= max1pt ? '#3B7A50' : '#D4614A' }}>{max1pt} plazas</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: '#7A6A5A' }}>Máx. 2 pts:</span>
                    <span style={{ fontWeight: '700', color: pActual <= max2pts ? '#3B7A50' : '#D4614A' }}>{max2pts} plazas</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={saveNow} style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.35rem 0.75rem', background: 'transparent',
                color: savedOk ? '#8D9A2E' : '#7A6A5A',
                border: `1px solid ${savedOk ? '#8D9A2E' : '#DDD5C8'}`,
                borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
                fontFamily: "'DM Sans', sans-serif"
              }}>
                <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
              </button>
              <button
                onClick={() => onSave({ pts: pts ?? 0, tipoKey: state.tipoKey, ia: iaEfectivo, nUsuarios: state.nUsuarios, plazasProyecto: state.plazasProyecto, maxAllowed: isSanitario ? maxTotal : max1pt, state })}
                disabled={saving || !ptsValid}
                style={{ background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.775rem', fontWeight: '600', cursor: (saving || !ptsValid) ? 'not-allowed' : 'pointer', opacity: (saving || !ptsValid) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}
              >
                <FloppyDisk size={14} />
                {saving ? 'Guardando…' : `Guardar puntuación TRA 4 (${ptsValid ? pts : '—'} pt${pts !== 1 ? 's' : ''})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
